import * as React from 'react'
import { format, parseISO } from 'date-fns'
import { X } from 'lucide-react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { trackIdsOf } from '@lib/helpers/event-track'
import { isPlausibleEventDay, MAX_EVENT_DAY, REQUESTED_DATES_MAX } from '@lib/input/event.input'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useBookedDays } from '@/hooks/useBookedDays'
import { useSetRequestedDates, useUpdateEvent } from '@/hooks/useEvents'
import { bookedLabel, todayIso } from '@/lib/bookedDays'

/** A requested date as the chip reads it: `Sat, Jun 13, 2027`. */
export function requestedDateLabel(day: CalendarDay): string {
  return format(parseISO(day), 'EEE, MMM d, yyyy')
}

interface RequestedDatesProps {
  event: EventDetailDto
  /** Adding, removing and booking. Without it the dates are only shown. */
  canEdit: boolean
  onError: (message: string | null) => void
}

/**
 * The dates an inquiry's couple asked about. They hold nothing and block
 * nobody, which is why they aren't the event's date; "Book this date" makes one
 * the event's date, which books it like picking it in the date field does. See
 * `docs/funnel-stages.spec.md`.
 */
export function RequestedDates({ event, canEdit, onError }: RequestedDatesProps) {
  const save = useSetRequestedDates(event.id)
  const update = useUpdateEvent(event.id)
  const [month, setMonth] = React.useState<CalendarDay | null>(null)
  const trackIds = trackIdsOf(event)
  const booked = useBookedDays({ venueId: event.venueId, trackIds, month: month ?? todayIso(), excludeEventId: event.id })
  const dates = event.requestedDates
  const busy = save.isPending || update.isPending

  if (!canEdit && dates.length === 0) return null

  function saveDates(next: CalendarDay[]): void {
    onError(null)
    save.mutate({ dates: [...next].sort() }, { onError: (e) => onError(requestErrorMessage(e)) })
  }

  function book(day: CalendarDay): void {
    onError(null)
    update.mutate({ date: day }, { onError: (e) => onError(requestErrorMessage(e)) })
  }

  return (
    <div className="space-y-2">
      <p className="m-0 text-sm font-medium">Requested dates</p>
      {dates.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">None yet. Add the dates the couple asked about.</p>
      ) : (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {dates.map((day) => (
            <li
              key={day}
              className="inline-flex h-[30px] items-center gap-1 rounded-sm border border-line bg-surface pl-2.5 pr-1 text-control"
            >
              <span className="pr-1">{requestedDateLabel(day)}</span>
              {canEdit ? (
                <>
                  <Button type="button" variant="link" size="sm" className="h-6 px-1" disabled={busy} onClick={() => book(day)}>
                    Book this date
                  </Button>
                  <button
                    type="button"
                    aria-label={`Remove ${requestedDateLabel(day)}`}
                    disabled={busy}
                    onClick={() => saveDates(dates.filter((d) => d !== day))}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-ink-muted hover:bg-surface-hover hover:text-ink disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canEdit && dates.length < REQUESTED_DATES_MAX ? (
        <DatePicker
          value=""
          placeholder="Add a date"
          disabled={busy}
          onChange={(day) => {
            if (day !== '' && isPlausibleEventDay(day) && !dates.includes(day)) saveDates([...dates, day])
          }}
          min={todayIso()}
          max={MAX_EVENT_DAY}
          marked={booked}
          markedLabel={bookedLabel(trackIds.length)}
          onMonthChange={setMonth}
          className="w-48"
        />
      ) : null}
    </div>
  )
}
